import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  error?: string;
  hint?: string;
  /** Use tabular-nums font feature (recommended for numeric inputs). */
  numeric?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, numeric, className, onFocus, onBlur, id, value, defaultValue, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `tf-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  const [focused, setFocused] = useState(false);
  const hasValue = value !== undefined ? String(value).length > 0 : undefined;
  const floating =
    focused || hasValue === true || (hasValue === undefined && Boolean(defaultValue));

  return (
    <div className={clsx('relative', className)}>
      <label
        htmlFor={inputId}
        className={clsx(
          'absolute left-3 pointer-events-none transition-all duration-200 ease-out',
          floating
            ? 'top-1 font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle'
            : 'top-1/2 -translate-y-1/2 font-display text-[0.9375rem] text-ink-muted',
        )}
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={clsx(hintId, errorId) || undefined}
        className={clsx(
          'w-full bg-panel border rounded-md px-3 pt-5 pb-2 min-h-[2.75rem] text-[0.9375rem] font-display',
          'transition-colors duration-200',
          'focus:outline-none focus:border-blue focus:ring-2 focus:ring-blue/30',
          numeric && 'font-tnum',
          error
            ? 'border-[#b33a3a]'
            : 'border-hairline hover:border-ink/30',
        )}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[0.75rem] text-ink-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-[#b33a3a]" role="alert">
          &gt; {error}
        </p>
      )}
    </div>
  );
});
